import { X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { type AlternativeCar, AlternativeCars } from '../../../components/ui/AlternativeCars.js';
import { CustomerToldNote } from '../../../components/ui/CustomerToldNote.js';
import { GapWarningBox, type GapWarningText } from '../../../components/ui/GapWarning.js';
import {
  onVisibleAreaChange,
  placeInView,
  touchForm,
  typingInside,
} from './keepOpenWhileTyping.js';

/**
 * 216 (#312) — the confirm step for a booking dragged onto another car's lane.
 *
 * Mirrors `CreateBlockPopover.tsx`'s SHELL exactly (portalled to `document.body`, placed via the
 * shared `placePopover` helper, dismissed on Escape/outside-click/scroll/resize) but has no editable
 * fields — the booking's window and identity are fixed by the drag itself; the only operator input
 * is confirm or cancel. Price and refusal both come from the reassignment endpoint's own existing
 * checks (research.md R5) — this component never computes either itself.
 */
/**
 * 316: which move is being confirmed.
 * - `car`: an unpaid booking to another car — Change Car, repriced, the new price shown (FR-007);
 * - `car-paid`: a booking with settled money to another car — the paid correction, price frozen,
 *   a reason required (1–500 characters, FR-006);
 * - `move` (328, #736): a target car and a time shift in one step — the server's move preview says
 *   what happens to the price, whether a reason is needed, and whether it clashes. It replaces 316's
 *   `time` mode (a time-only move is a `move` onto the same car).
 */
export type MoveMode = 'car' | 'car-paid' | 'move';

/** 328: what the server's move preview said, as the page words it. */
export interface MoveState {
  /** The preview is still being asked; nothing can be confirmed. */
  loading: boolean;
  /** The price line: kept, repriced from → to, or kept as paid. */
  priceText?: string;
  /** A paid booking changing car: a reason is required (1–500 characters). */
  reasonRequired: boolean;
  /** A refusal other than a clash, shown instead of the price; the move cannot be confirmed. */
  refusal?: string | null;
  /** US2: the move clashes — what with, and the suggested free time's label (null: none found). */
  clash?: { message: string; suggestionLabel: string | null } | null;
  /** 379: a correction of a finished, paid rental — the customer is not told. */
  silent?: boolean;
}

export const REASON_MAX = 500;

export interface MoveBookingPopoverProps {
  carLabel: string;
  anchorRect: DOMRect;
  mode?: MoveMode;
  /** 316: what is moving and from → to (a car, or a period), shown before anything is saved. */
  summary?: { item: string; from: string; to: string; when?: string };
  /** Undefined while the preview is still resolving. Only the `car` mode waits for it. */
  priceLabel?: string;
  /** 328: the `move` mode's state, from the server's preview. */
  move?: MoveState;
  /** Called with the trimmed reason when one is asked for, otherwise with nothing. */
  onConfirm: (reason?: string) => void;
  /** 328 US2: take the suggested free time (one click), with the reason when one is asked for. */
  onUseSuggestion?: (reason?: string) => void;
  onDismiss: () => void;
  error?: string | null;
  alternatives?: AlternativeCar[];
  pending?: boolean;
  /** 386 (#1278): the proposed window leaves less than the car's gap next to another rental. */
  gapWarning?: GapWarningText | null;
}

export function MoveBookingPopover({
  carLabel,
  anchorRect,
  mode = 'car',
  summary,
  priceLabel,
  move,
  onConfirm,
  onUseSuggestion,
  onDismiss,
  error = null,
  alternatives = [],
  pending = false,
  gapWarning = null,
}: MoveBookingPopoverProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const focusedOnce = useRef(false);
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  const reasonTooLong = trimmed.length > REASON_MAX;
  const asksReason = mode === 'car-paid' || (mode === 'move' && Boolean(move?.reasonRequired));
  const reasonOk = trimmed.length > 0 && !reasonTooLong;
  const canConfirm =
    !pending &&
    (mode === 'car'
      ? Boolean(priceLabel)
      : mode === 'car-paid'
        ? reasonOk
        : Boolean(move) &&
          !move?.loading &&
          !move?.refusal &&
          !move?.clash &&
          (!move?.reasonRequired || reasonOk));
  const canUseSuggestion = !pending && (!move?.reasonRequired || reasonOk);
  const [placement, setPlacement] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;

    const place = () => {
      const { width, height } = el.getBoundingClientRect();
      setPlacement(placeInView(anchorRect, { width, height }));
    };
    place();

    const ro = new ResizeObserver(place);
    ro.observe(el);
    // 404 (#1329): the keyboard opening or closing moves the visible area; follow it.
    const stop = onVisibleAreaChange(place);
    return () => {
      ro.disconnect();
      stop();
    };
  }, [anchorRect]);

  // FR-025: focus lands on Confirm (or on the required reason, whose Confirm starts disabled), so a
  // keyboard move is confirmed with Enter and cancelled with Escape. 🔴 Only once the popover is
  // PLACED: it mounts with `visibility: hidden` until measured, and `focus()` on a hidden element
  // silently does nothing (an `autoFocus` first tried here never took focus in a real browser;
  // jsdom ignores visibility, so the unit test passed).
  // 328: a `move` knows whether it needs a reason only once its preview answers, so the one focus waits
  // for that (found at Gate A: a paid move focused nothing).
  const moveLoading = mode === 'move' && (!move || move.loading);
  useEffect(() => {
    if (!placement || focusedOnce.current || moveLoading) return;
    focusedOnce.current = true;
    (asksReason ? reasonRef.current : confirmRef.current)?.focus();
  }, [placement, asksReason, moveLoading]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      // 404 (#1329): the first tap outside while typing on a touch screen only closes the keyboard.
      if (typingInside(boxRef.current)) return;
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) onDismiss();
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [onDismiss]);

  useEffect(() => {
    // 404 (#1329): never for a pop-up with a field on a touch screen — the phone keyboard resizes and scrolls the
    // window as it opens and closes; the pop-up is re-placed above it instead (placement effect).
    const close = () => {
      if (!touchForm(boxRef.current)) onDismiss();
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [onDismiss]);

  return createPortal(
    <div
      ref={boxRef}
      data-testid="move-booking-popover"
      // biome-ignore lint/a11y/useSemanticElements: #950 role="dialog" on a positioned panel is the intended ARIA pattern; native <dialog> is hidden until opened and brings its own positioning
      role="dialog"
      aria-label={`Move booking to ${carLabel}`}
      style={{
        position: 'fixed',
        left: placement?.left ?? anchorRect.left,
        top: placement?.top ?? anchorRect.bottom + 8,
        visibility: placement ? 'visible' : 'hidden',
        zIndex: 50,
      }}
      className="w-72 rounded-xl border border-hairline bg-canvas p-3 shadow-xl motion-safe:transition-opacity"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-ink">{`Move to ${carLabel}`}</p>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close"
          className="text-steel hover:text-ink motion-safe:transition-colors"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>

      {summary && (
        <dl data-testid="move-booking-summary" className="mb-2 grid gap-0.5 text-xs">
          <dt className="sr-only">Booking</dt>
          <dd className="font-semibold text-ink">{summary.item}</dd>
          <dt className="text-steel">From</dt>
          <dd className="text-ink">{summary.from}</dd>
          <dt className="text-steel">To</dt>
          <dd className="text-ink">{summary.to}</dd>
          {/* A car move keeps its times; the popup still says when (quickstart A3). */}
          {summary.when && (
            <>
              <dt className="text-steel">When</dt>
              <dd className="text-ink">{summary.when}</dd>
            </>
          )}
        </dl>
      )}

      {!error && mode === 'car' && (
        <p data-testid="move-booking-price" className="text-sm text-steel">
          {priceLabel ?? 'Resolving price…'}
        </p>
      )}
      {!error && mode === 'move' && move && !move.refusal && !move.clash && (
        <p data-testid="move-booking-price" className="text-sm text-steel">
          {move.loading ? 'Checking the move…' : move.priceText}
        </p>
      )}
      {mode === 'move' && move?.refusal && (
        <p role="alert" className="text-sm text-semantic-danger-text">
          {move.refusal}
        </p>
      )}
      {mode === 'move' && move?.clash && (
        <div data-testid="move-booking-clash" className="grid gap-1.5">
          <p role="alert" className="text-sm text-semantic-danger-text">
            {move.clash.message}
          </p>
          {move.clash.suggestionLabel ? (
            <button
              type="button"
              onClick={() => onUseSuggestion?.(asksReason ? trimmed : undefined)}
              disabled={!canUseSuggestion}
              className="justify-self-start rounded border border-brand-blue px-2 py-1 text-sm text-brand-blue hover:bg-brand-blue-soft disabled:opacity-50 motion-safe:transition-colors"
            >
              {`Use ${move.clash.suggestionLabel}`}
            </button>
          ) : (
            <p className="text-sm text-steel">No free time found in this period.</p>
          )}
        </div>
      )}
      {mode === 'car-paid' && (
        <p data-testid="move-booking-price" className="text-sm text-steel">
          Price stays as paid, and a car at another location is allowed. The reason is recorded in
          the booking's history.
        </p>
      )}
      {asksReason && (
        <>
          <label className="mt-2 flex flex-col gap-1 text-xs text-steel">
            <span>Reason for the correction (required)</span>
            <textarea
              ref={reasonRef}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              aria-invalid={reasonTooLong || undefined}
              className="rounded border border-hairline bg-canvas px-2 py-1 text-sm text-ink"
            />
          </label>
          <p className="mt-0.5 text-xs text-steel">
            {reasonTooLong
              ? `Too long: ${trimmed.length} of ${REASON_MAX} characters.`
              : `${trimmed.length} / ${REASON_MAX}`}
          </p>
        </>
      )}

      {error && (
        <>
          <p role="alert" className="text-sm text-semantic-danger-text">
            {error}
          </p>
          <AlternativeCars items={alternatives} />
        </>
      )}

      {/* 386 (#1278, FR-011): a warning only — staff may go ahead. */}
      <div className="mt-2">
        <GapWarningBox warning={gapWarning} />
      </div>
      {!move?.silent && <CustomerToldNote />}
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={onDismiss}
          className="rounded px-2 py-1 text-sm text-slate hover:text-ink motion-safe:transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          ref={confirmRef}
          onClick={() => onConfirm(asksReason ? trimmed : undefined)}
          disabled={!canConfirm}
          className="rounded bg-brand-blue px-3 py-1 text-sm text-on-primary disabled:opacity-50 motion-safe:transition-colors"
        >
          {pending ? 'Moving…' : gapWarning ? 'Move anyway' : 'Confirm move'}
        </button>
      </div>
    </div>,
    document.body,
  );
}
